/**
 * Страница открыта в окне приложения MY REVIVE, а не во вкладке браузера.
 *
 * Приложение открывает любую страницу сайта с ?source=app (см.
 * features/auth/lib/webHandoff.ts) — но только когда у него есть вход; без
 * входа адрес идёт без метки. Поэтому второй признак — referrer, который
 * Chrome ставит окну, открытому приложением: android-app://com.torsunov.revive
 * (так и у уже выпущенных сборок). Признак есть только в первом адресе
 * окна, поэтому его запоминает sessionStorage этой вкладки: переходы по сайту
 * внутри окна его не теряют, а обычная вкладка браузера его не получает.
 *
 * В таком окне у кабинета нет своей шапки — только «‹», как в приложении
 * (AppWindowBar). «‹» на первой странице окна возвращает в приложение по
 * ссылке [APP_CLOSE_URL]: приложение закрывает окно сайта и показывает экран,
 * с которого его открыли.
 */
import { HANDOFF_SOURCE_APP, HANDOFF_SOURCE_PARAM } from "@/features/auth/lib/webHandoff";

const KEY = "revive_app_window";

/** Chrome ставит окну, открытому приложением, referrer android-app://<пакет>/. */
const APP_REFERRER = /^android-app:\/\/com\.torsunov\.revive(\/|$)/;

/** Ссылка, по которой приложение закрывает окно сайта (обрабатывает само приложение). */
export const APP_CLOSE_URL = "myrevive://app/close";

/**
 * Ключ localStorage: на этом телефоне есть приложение MY REVIVE — оно уже
 * открывало здесь окно сайта. Окно приложения (Trusted Web Activity) — это
 * тот же Chrome телефона с тем же хранилищем, поэтому отметка, поставленная
 * в окне, видна и в обычной вкладке: там «Тренировки» и «Календарь» зовут
 * «Открыть в приложении», а не «Установить» (useHasApp). Значение — когда
 * отметили (ISO).
 */
export const HAS_APP_KEY = "revive_has_app";

/** Открыта ли страница в окне приложения; видит метку в адресе — запоминает её. */
export const isAppWindow = (): boolean => {
    try {
        if (
            new URLSearchParams(window.location.search).getAll(HANDOFF_SOURCE_PARAM).includes(HANDOFF_SOURCE_APP) ||
            APP_REFERRER.test(document.referrer || "")
        ) {
            sessionStorage.setItem(KEY, "1");
            markHasApp();
            return true;
        }
        return sessionStorage.getItem(KEY) === "1";
    } catch {
        return false;
    }
};

const markHasApp = (): void => {
    try {
        localStorage.setItem(HAS_APP_KEY, new Date().toISOString());
    } catch {
        /* приватный режим — отметки нет, будет «Установить» */
    }
};

/** Открывало ли приложение окно сайта в этом браузере (отметка isAppWindow). */
export const hasAppMark = (): boolean => {
    try {
        return !!localStorage.getItem(HAS_APP_KEY);
    } catch {
        return false;
    }
};

/** Ссылка, которая открывает приложение MY REVIVE (его intent-filter myrevive://app). */
export const APP_OPEN_URL = "myrevive://app";

/**
 * Вкладка «Календарь» приложения (маршрут calendar; MainActivity знает этот путь с
 * feat/calendar-tab-link). Сборка постарше путь не знает — она просто открывается на том экране,
 * где её оставили, как по [APP_OPEN_URL].
 */
export const APP_CALENDAR_URL = "myrevive://app/calendar";

/**
 * Перейти по ссылке приложения (myrevive://app/…). Только по нажатию: Chrome выпускает ссылку
 * другой схемы из страницы лишь в ответ на жест человека.
 */
export const openAppLink = (url: string): void => {
    window.location.href = url;
};

/**
 * Конструктор плана тренировок в приложении (маршрут plan_wizard; MainActivity знает этот путь с
 * fix/1110-planreminder). Сборка постарше ссылку не знает — она просто возвращает в приложение:
 * окно сайта закрывается, как по «‹».
 */
export const APP_PLAN_WIZARD_URL = "myrevive://app/plan_wizard";

/**
 * Рубильник: выпущенное приложение знает ссылку [APP_PLAN_WIZARD_URL]. Android 2.0.254 — ещё нет
 * (кнопка вернула бы в приложение без конструктора), iOS — не знает вовсе. Пока выключен, в окне
 * приложения подсказка «Создайте план тренировок — …» идёт без кнопки. Включить, когда в Google Play
 * сборка ≥ 2.0.255 (с fix/1110-planreminder); кнопка и тогда — только на Android.
 */
export const APP_WINDOW_PLAN_WIZARD = false;

/**
 * «‹» в окне приложения: есть куда вернуться внутри окна (react-router
 * хранит номер шага в history.state.idx) — на шаг назад; это первая страница
 * окна — обратно в приложение.
 */
export const appWindowBack = (navigateBack: () => void): void => {
    const idx = (window.history.state as { idx?: number } | null)?.idx;
    if (typeof idx === "number" && idx > 0) navigateBack();
    else appWindowClose();
};

/**
 * Закрыть окно сайта и вернуться в приложение — сразу, сколько бы шагов ни
 * было пройдено внутри окна. Так уходит «‹» корня вкладки (правило «назад —
 * на уровень вверх»: выше вкладки — само приложение).
 */
export const appWindowClose = (): void => {
    window.location.href = APP_CLOSE_URL;
};
